import { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface Offer {
  id: string;
  title: string;
  description: string;
  discount_text?: string;
  image_url?: string;
  valid_from: string;
  valid_until: string;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

const getImageUrl = (url: string | null | undefined) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  return resolveImageUrl(url);
};

const OffersManager = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getOffers();
      setOffers(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load offers');
      console.error('Error loading offers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOffer = (offer: Offer) => {
    setEditingOffer({ ...offer });
    setShowModal(true);
  };

  const handleAddOffer = () => {
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const validUntil = nextMonth.toISOString().split('T')[0];

    const newOffer: Offer = {
      id: '',
      title: '',
      description: '',
      discount_text: '',
      image_url: '',
      valid_from: today,
      valid_until: validUntil,
      is_active: 1
    };
    setEditingOffer(newOffer);
    setShowModal(true);
  };

  const handleSaveOffer = async () => {
    if (!editingOffer) return;

    try {
      setIsSaving(true);
      setError(null);

      let finalImageUrl = editingOffer.image_url;

      // 1. Upload image if selected
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        try {
          const uploadRes = await apiClient.uploadImage(formData, 'home');
          if (uploadRes && uploadRes.imageUrl) {
            finalImageUrl = uploadRes.imageUrl;
          }
        } catch (uploadErr) {
          console.error("Image upload failed", uploadErr);
          alert("Failed to upload image. Please try again.");
          return;
        }
      }

      const isEditing = offers.find(o => o.id === editingOffer.id);

      const payload = {
        title: editingOffer.title,
        description: editingOffer.description,
        discount_text: editingOffer.discount_text || '',
        // Ensure we don't send large base64 strings in the JSON payload
        image_url: (finalImageUrl && !finalImageUrl.startsWith('data:')) ? finalImageUrl : (editingOffer.image_url?.startsWith('data:') ? '' : editingOffer.image_url),
        valid_from: editingOffer.valid_from,
        valid_until: editingOffer.valid_until
      };

      if (isEditing) {
        await apiClient.updateOffer(editingOffer.id, payload);
      } else {
        await apiClient.createOffer(payload);
      }

      await loadOffers();
      setShowModal(false);
      setEditingOffer(null);
      setImageFile(null);
    } catch (error) {
      console.error('Failed to save offer:', error);
      setError('Failed to save offer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteOffer = async (id: string) => {
    console.log('[OffersManager] handleDeleteOffer called with id:', id);
    const ok = window.confirm('Are you sure you want to delete this offer?');
    console.log('[OffersManager] confirm returned:', ok);
    if (!ok) return;

    try {
      setError(null);
      console.log('[OffersManager] Proceeding with API delete for offer:', id);
      await apiClient.deleteOffer(id);
      console.log('[OffersManager] Delete successful, reloading data');
      await loadOffers();
    } catch (error: any) {
      console.error('[OffersManager] Failed to delete offer:', error);
      setError(error?.message || 'Failed to delete offer');
    }
  };

  const toggleOfferStatus = async (id: string) => {
    try {
      const offer = offers.find(o => o.id === id);
      if (offer) {
        await apiClient.updateOffer(id, {
          ...offer,
          is_active: offer.is_active ? 0 : 1
        });
        await loadOffers();
      }
    } catch (error) {
      console.error('Failed to toggle offer status:', error);
      setError('Failed to update offer status');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX_SIZE) {
        alert("File is too large. Max 5MB allowed.");
        return;
      }
      setImageFile(file);

      // Still do preview for user experience
      const reader = new FileReader();
      reader.onload = (e) => {
        if (editingOffer && e.target?.result) {
          setEditingOffer({ ...editingOffer, image_url: e.target.result as string });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const isOfferExpired = (validUntil: string) => {
    return new Date(validUntil) < new Date();
  };

  const isOfferActive = (validFrom: string, validUntil: string) => {
    const now = new Date();
    return new Date(validFrom) <= now && new Date(validUntil) >= now;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        <span className="ml-2 text-gray-600">Loading offers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadOffers}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">Offers Management</h3>
        <button
          onClick={handleAddOffer}
          className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center"
        >
          <i className="ri-add-line mr-2"></i>
          Add Offer
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {offers.map((offer) => (
          <div key={offer.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            {offer.image_url && (
              <img
                src={getImageUrl(offer.image_url)}
                alt={offer.title}
                className="w-full h-48 object-cover"
              />
            )}

            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${offer.discount_text
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
                  }`}>
                  {offer.discount_text || 'Special Offer'}
                </span>

                <span className={`px-2 py-1 text-xs rounded-full ${offer.is_active && isOfferActive(offer.valid_from, offer.valid_until)
                  ? 'bg-green-100 text-green-800'
                  : isOfferExpired(offer.valid_until)
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                  }`}>
                  {isOfferExpired(offer.valid_until)
                    ? 'Expired'
                    : offer.is_active
                      ? 'Active'
                      : 'Inactive'}
                </span>
              </div>

              <h4 className="text-lg font-bold text-gray-900 mb-2">{offer.title}</h4>
              <p className="text-gray-600 text-sm mb-4 line-clamp-3">{offer.description}</p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <i className="ri-calendar-line mr-2"></i>
                  <span>Valid from: {new Date(offer.valid_from).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <i className="ri-calendar-check-line mr-2"></i>
                  <span>Valid until: {new Date(offer.valid_until).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleOfferStatus(offer.id)}
                    className="text-gray-600 hover:text-gray-800 text-sm"
                  >
                    <i className={offer.is_active ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                  </button>
                  <button
                    onClick={() => handleEditOffer(offer)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => handleDeleteOffer(offer.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && editingOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {offers.find(o => o.id === editingOffer.id) ? 'Edit Offer' : 'Add Offer'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Offer Title</label>
                <input
                  type="text"
                  value={editingOffer.title}
                  onChange={(e) => setEditingOffer({ ...editingOffer, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Enter offer title"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Description</label>
                <textarea
                  value={editingOffer.description}
                  onChange={(e) => setEditingOffer({ ...editingOffer, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                  placeholder="Enter offer description"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Discount Text</label>
                <input
                  type="text"
                  value={editingOffer.discount_text || ''}
                  onChange={(e) => setEditingOffer({ ...editingOffer, discount_text: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="e.g., 25% OFF, FLAT ₹500 OFF"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Valid From</label>
                  <input
                    type="date"
                    value={editingOffer.valid_from}
                    onChange={(e) => setEditingOffer({ ...editingOffer, valid_from: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Valid Until</label>
                  <input
                    type="date"
                    value={editingOffer.valid_until}
                    onChange={(e) => setEditingOffer({ ...editingOffer, valid_until: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                <p className="text-sm text-gray-600 mt-1">or enter image URL below</p>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Image URL</label>
                <input
                  type="url"
                  value={editingOffer.image_url || ''}
                  onChange={(e) => setEditingOffer({ ...editingOffer, image_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
                {editingOffer.image_url && (
                  <img
                    src={getImageUrl(editingOffer.image_url)}
                    alt="Preview"
                    className="mt-2 w-full h-32 object-cover rounded-lg"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOffer}
                disabled={isSaving}
                className={`px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSaving ? 'Saving...' : 'Save Offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersManager;